import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Leaf, Users, Award } from 'lucide-react';

const Engagement = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-800 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="engagement-title">{t.engagement.title}</h1>
          <p className="text-xl">{t.engagement.subtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Quality Charter */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12" data-testid="quality-section">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="text-amber-600" size={48} />
            <h2 className="text-3xl font-bold text-stone-800">{t.engagement.quality_title}</h2>
          </div>
          <p className="text-lg text-gray-700 mb-6">
            {language === 'fr' ? (
              "La rigueur industrielle au service du produit."
            ) : (
              "Industrial rigor in service of the product."
            )}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-amber-50 p-6 rounded-lg">
              <h3 className="font-bold text-lg text-stone-800 mb-2">
                {language === 'fr' ? 'Traçabilité Totale' : 'Total Traceability'}
              </h3>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Grâce à notre intégration verticale, nous contrôlons 100% de la chaîne de production, du champ au conditionnement."
                ) : (
                  "Thanks to our vertical integration, we control 100% of the production chain, from field to packaging."
                )}
              </p>
            </div>
            <div className="bg-amber-50 p-6 rounded-lg">
              <h3 className="font-bold text-lg text-stone-800 mb-2">
                {language === 'fr' ? 'Analyses de Pointe' : 'Advanced Analysis'}
              </h3>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Nos produits agroalimentaires font l'objet de tests rigoureux en laboratoire pour garantir des niveaux de polyphénols optimaux."
                ) : (
                  "Our agri-food products undergo rigorous laboratory testing to ensure optimal polyphenol levels."
                )}
              </p>
            </div>
            <div className="bg-amber-50 p-6 rounded-lg">
              <h3 className="font-bold text-lg text-stone-800 mb-2">
                {language === 'fr' ? 'Sécurité et Hygiène' : 'Safety and Hygiene'}
              </h3>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Notre unité de conditionnement respecte les normes internationales d'hygiène (HACCP)."
                ) : (
                  "Our packaging unit complies with international hygiene standards (HACCP)."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Environmental Commitment */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12" data-testid="environmental-section">
          <div className="flex items-center space-x-3 mb-6">
            <Leaf className="text-green-600" size={48} />
            <h2 className="text-3xl font-bold text-stone-800">{t.engagement.env_title}</h2>
          </div>
          <p className="text-lg text-gray-700 mb-6">
            {language === 'fr' ? (
              "Produire aujourd'hui sans compromettre demain."
            ) : (
              "Producing today without compromising tomorrow."
            )}
          </p>
          <div className="space-y-4 text-gray-700">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h4 className="font-bold">
                  {language === 'fr' ? 'Culture Durable' : 'Sustainable Farming'}
                </h4>
                <p>
                  {language === 'fr' ? (
                    "Utilisation de méthodes d'irrigation optimisées pour préserver les ressources hydriques de nos domaines d'oliviers et de pistachiers."
                  ) : (
                    "Use of optimized irrigation methods to preserve water resources on our olive and pistachio estates."
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h4 className="font-bold">
                  {language === 'fr' ? 'Valorisation des Déchets' : 'Waste Valorization'}
                </h4>
                <p>
                  {language === 'fr' ? (
                    "Nous travaillons sur la réduction de notre empreinte carbone en optimisant nos circuits de distribution."
                  ) : (
                    "We work on reducing our carbon footprint by optimizing our distribution networks."
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h4 className="font-bold">
                  {language === 'fr' ? 'Respect de la Biodiversité' : 'Biodiversity Respect'}
                </h4>
                <p>
                  {language === 'fr' ? (
                    "Nos vergers sont gérés de manière à préserver l'écosystème local et la richesse des sols tunisiens."
                  ) : (
                    "Our orchards are managed to preserve the local ecosystem and the richness of Tunisian soils."
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Commitment */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12" data-testid="social-section">
          <div className="flex items-center space-x-3 mb-6">
            <Users className="text-blue-600" size={48} />
            <h2 className="text-3xl font-bold text-stone-800">{t.engagement.social_title}</h2>
          </div>
          <p className="text-lg text-gray-700 mb-6">
            {language === 'fr' ? (
              "Une entreprise familiale au service de sa communauté."
            ) : (
              "A family business serving its community."
            )}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-bold text-lg text-stone-800 mb-2">
                {language === 'fr' ? 'Savoir-faire Local' : 'Local Expertise'}
              </h4>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Nous valorisons l'emploi régional et la transmission des compétences entre les générations."
                ) : (
                  "We value regional employment and the transmission of skills between generations."
                )}
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg text-stone-800 mb-2">
                {language === 'fr' ? 'Éthique des Affaires' : 'Business Ethics'}
              </h4>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Nous entretenons des relations de long terme basées sur la transparence et le respect mutuel."
                ) : (
                  "We maintain long-term relationships based on transparency and mutual respect."
                )}
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg text-stone-800 mb-2">
                {language === 'fr' ? 'Héritage et Transmission' : 'Heritage and Transmission'}
              </h4>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Notre vision s'inscrit dans la durée. Nous cherchons la pérennité de notre réputation."
                ) : (
                  "Our vision is long-term. We seek the sustainability of our reputation."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Export Standards */}
        <div className="bg-stone-800 text-white rounded-lg shadow-lg p-8" data-testid="export-section">
          <div className="flex items-center space-x-3 mb-6">
            <Award className="text-amber-400" size={48} />
            <h2 className="text-3xl font-bold">{t.engagement.export_title}</h2>
          </div>
          <p className="text-lg mb-6">
            {language === 'fr' ? (
              "Prêts pour les marchés mondiaux."
            ) : (
              "Ready for global markets."
            )}
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-gray-300">
                {language === 'fr' ? (
                  "• Conformité avec les normes du Conseil Oléicole International (COI)"
                ) : (
                  "• Compliance with International Olive Council (IOC) standards"
                )}
              </p>
            </div>
            <div>
              <p className="text-gray-300">
                {language === 'fr' ? (
                  "• Préparation aux certifications spécifiques (JAS pour le Japon, KFDA pour la Corée)"
                ) : (
                  "• Preparation for specific certifications (JAS for Japan, KFDA for Korea)"
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Engagement;